/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import {
  Bell,
  Send,
  Zap,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  BookOpen,
  Trophy,
  Calendar,
  Smartphone,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

// ─── Templates (richer with category grouping + emoji presets) ────────────

interface Template {
  label: string;
  title: string;
  body: string;
  icon: React.ReactNode;
  tone: "blue" | "emerald" | "amber" | "rose" | "purple";
}

const TEMPLATES: Template[] = [
  {
    label: "Deadline Reminder",
    title: "⏰ Team Deadline Tonight!",
    body: "Make your final team changes before the matchweek deadline. Good luck!",
    icon: <Calendar className="h-4 w-4" />,
    tone: "amber",
  },
  {
    label: "New Matchweek",
    title: "⚽ New Matchweek Is Open!",
    body: "A new matchweek has started. Pick your team now before the deadline.",
    icon: <Calendar className="h-4 w-4" />,
    tone: "emerald",
  },
  {
    label: "Results Are In",
    title: "📊 Matchweek Results Are In!",
    body: "Check your points and see where you rank on the leaderboard.",
    icon: <Trophy className="h-4 w-4" />,
    tone: "blue",
  },
  {
    label: "Daily Challenge",
    title: "🧠 Today's Challenge Awaits",
    body: "Solve today's challenge and keep your streak alive!",
    icon: <Zap className="h-4 w-4" />,
    tone: "purple",
  },
  {
    label: "Giveaway Winner",
    title: "🎁 Giveaway Winner Announced!",
    body: "This matchweek's winner has been selected! Check the app to see if it's you.",
    icon: <Trophy className="h-4 w-4" />,
    tone: "rose",
  },
  {
    label: "Match Starting",
    title: "🔴 Match Starting Soon!",
    body: "A World Cup match is about to kick off. Your team is locked in — good luck!",
    icon: <Zap className="h-4 w-4" />,
    tone: "rose",
  },
  {
    label: "New Story",
    title: "📰 Fresh Football Story",
    body: "We just dropped a new story in your feed. Take a look!",
    icon: <BookOpen className="h-4 w-4" />,
    tone: "emerald",
  },
  {
    label: "LFS Round Live",
    title: "🛡 Last Fan Standing — Pick Now",
    body: "A new Last Fan Standing round is live. Pick a team before the deadline.",
    icon: <ShieldCheck className="h-4 w-4" />,
    tone: "amber",
  },
];

const TONE_CLASSES: Record<
  Template["tone"],
  { pill: string; border: string; icon: string }
> = {
  blue: {
    pill: "bg-blue-50 text-blue-700",
    border: "border-blue-200 hover:border-blue-400",
    icon: "text-blue-600",
  },
  emerald: {
    pill: "bg-emerald-50 text-emerald-700",
    border: "border-emerald-200 hover:border-emerald-400",
    icon: "text-emerald-600",
  },
  amber: {
    pill: "bg-amber-50 text-amber-700",
    border: "border-amber-200 hover:border-amber-400",
    icon: "text-amber-600",
  },
  rose: {
    pill: "bg-rose-50 text-rose-700",
    border: "border-rose-200 hover:border-rose-400",
    icon: "text-rose-600",
  },
  purple: {
    pill: "bg-purple-50 text-purple-700",
    border: "border-purple-200 hover:border-purple-400",
    icon: "text-purple-600",
  },
};

// ─── Page ─────────────────────────────────────────────────────────────────

interface Stats {
  totalUsers: number;
  withToken: number;
  withoutToken: number;
  tokenCoverage: number;
  registeredLast7d: number;
  newUsers7d: number;
}

export default function NotificationsPage() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const {
    data: stats,
    refetch,
    isFetching,
  } = useQuery<Stats>({
    queryKey: ["notifications", "stats"],
    queryFn: () => apiClient.get(API_ENDPOINTS.NOTIFICATIONS.STATS),
    staleTime: 30_000,
  });

  const { mutate: broadcast, isPending } = useMutation({
    mutationFn: (payload: { title: string; body: string }) =>
      apiClient.post(API_ENDPOINTS.NOTIFICATIONS.BROADCAST, payload),
    onSuccess: (res: any) => {
      toast.success(res.message || "Notification sent!");
      setTitle("");
      setBody("");
      refetch();
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to send notification"),
  });

  const handleSend = () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and message are required");
      return;
    }
    if (
      !confirm(
        `Send push notification to ALL users?\n\nTitle: ${title}\nMessage: ${body}`,
      )
    )
      return;
    broadcast({ title, body });
  };

  const applyTemplate = (t: Template) => {
    setTitle(t.title);
    setBody(t.body);
  };

  const tokenCoverageLow = (stats?.tokenCoverage ?? 100) < 30;
  const noReachableUsers = (stats?.withToken ?? 0) === 0;

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Push Notifications
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Broadcast targeted messages to the mobile app. Delivery only works
            for users who have granted permission.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() =>
            refetch().then(() => toast.success("Refreshed"))
          }
          disabled={isFetching}
          title="Refresh stats"
        >
          <RefreshCw
            className={cn("h-4 w-4", isFetching && "animate-spin")}
          />
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiTile
          label="Reachable devices"
          value={stats?.withToken ?? 0}
          sub={`${stats?.tokenCoverage ?? 0}% of ${stats?.totalUsers ?? 0} users`}
          icon={<Smartphone className="h-5 w-5" />}
          tone="blue"
        />
        <KpiTile
          label="Without tokens"
          value={stats?.withoutToken ?? 0}
          sub="blocked, simulator, or denied"
          icon={<AlertTriangle className="h-5 w-5" />}
          tone={noReachableUsers ? "red" : "amber"}
        />
        <KpiTile
          label="New devices · 7d"
          value={stats?.registeredLast7d ?? 0}
          sub="refreshed in the last week"
          icon={<UserCheck className="h-5 w-5" />}
          tone="emerald"
        />
        <KpiTile
          label="New signups · 7d"
          value={stats?.newUsers7d ?? 0}
          sub="fresh user accounts"
          icon={<TrendingUp className="h-5 w-5" />}
          tone="purple"
        />
      </div>

      {/* Setup health */}
      <div className="mb-6">
        <SetupHealthCard
          noReachableUsers={noReachableUsers}
          lowCoverage={tokenCoverageLow && !noReachableUsers}
        />
      </div>

      {/* Compose + Templates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Send className="h-4 w-4 text-blue-500" />
                Compose Broadcast
              </h2>
              <Badge className="bg-blue-50 text-blue-700 font-semibold">
                {stats?.withToken ?? 0} devices will receive this
              </Badge>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block uppercase tracking-wide">
                Title{" "}
                <span className="text-gray-400 font-normal normal-case">
                  (bold line shown on device)
                </span>
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. ⏰ Deadline Tonight!"
                maxLength={65}
              />
              <p className="text-[11px] text-gray-400 mt-1 text-right">
                {title.length}/65
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block uppercase tracking-wide">
                Message
              </Label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="e.g. Make your final team changes before the matchweek deadline!"
                maxLength={200}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <p className="text-[11px] text-gray-400 mt-1 text-right">
                {body.length}/200
              </p>
            </div>

            {/* Live preview */}
            {(title || body) && (
              <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white">
                <p className="text-[11px] text-gray-400 mb-2 font-bold uppercase tracking-wide">
                  Device preview
                </p>
                <div className="bg-white rounded-2xl shadow p-3 flex items-start gap-3 border border-gray-100">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white text-lg shrink-0">
                    ⚽
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {title || "Title"}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                      {body || "Message body"}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Ball Knowledge · now
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleSend}
              disabled={
                isPending ||
                !title.trim() ||
                !body.trim() ||
                noReachableUsers
              }
              className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {isPending ? (
                "Sending…"
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  {noReachableUsers
                    ? "No devices to send to"
                    : `Send to ${stats?.withToken ?? 0} devices`}
                </>
              )}
            </Button>

            <p className="text-[11px] text-gray-400 text-center">
              Only users who have granted notification permissions will
              receive this.
            </p>
          </div>
        </div>

        {/* Templates */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Zap className="h-4 w-4 text-yellow-500" />
            Quick Templates
          </h2>
          <div className="grid grid-cols-1 gap-2">
            {TEMPLATES.map((t) => {
              const c = TONE_CLASSES[t.tone];
              return (
                <button
                  key={t.label}
                  onClick={() => applyTemplate(t)}
                  className={cn(
                    "text-left p-3 rounded-lg border transition-all bg-white hover:shadow-sm group",
                    c.border,
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        "w-6 h-6 rounded-md flex items-center justify-center",
                        c.pill,
                      )}
                    >
                      <span className={c.icon}>{t.icon}</span>
                    </span>
                    <p className="text-sm font-bold text-gray-900">
                      {t.label}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{t.title}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wide mb-2">
              Tips
            </p>
            <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
              <li>Keep titles under 40 chars</li>
              <li>Use emojis to grab attention</li>
              <li>Always include a clear call to action</li>
              <li>Don&apos;t spam — max 1–2 per day</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: number | string;
  sub: string;
  icon: React.ReactNode;
  tone: "blue" | "emerald" | "amber" | "purple" | "red";
}) {
  const toneMap: Record<string, string> = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
          {label}
        </p>
        <div
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            toneMap[tone],
          )}
        >
          {icon}
        </div>
      </div>
      <p className="text-2xl font-extrabold text-gray-900">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-[11px] text-gray-500 mt-1 truncate" title={sub}>
        {sub}
      </p>
    </div>
  );
}

function SetupHealthCard({
  noReachableUsers,
  lowCoverage,
}: {
  noReachableUsers: boolean;
  lowCoverage: boolean;
}) {
  const checks = [
    {
      label: "Mobile: call registerPushToken() after login",
      status: "done" as const,
      detail: "OAuth + email sign-in already register tokens.",
    },
    {
      label: "Mobile: expo-notifications + expo-device installed",
      status: "done" as const,
      detail: "Native modules required outside Expo Go.",
    },
    {
      label: "Backend: expo-server-sdk wired to sendToUsers / broadcast",
      status: "done" as const,
      detail: "Service at src/notifications/expo-notifications.service.ts.",
    },
    {
      label: "Android: Firebase Cloud Messaging credentials",
      status: noReachableUsers ? "warn" : "check",
      detail:
        "Required for Android delivery. Run `eas credentials` → Android → Push Notifications → Firebase Cloud Messaging, and upload the FCM V1 service account JSON from your Firebase console.",
    },
    {
      label: "iOS: APNs key uploaded to EAS",
      status: "check" as const,
      detail:
        "Required for iOS delivery. `eas credentials` → iOS → Push Notifications → generate or upload the APNs .p8 key.",
    },
    {
      label: "User granted permission on the device",
      status: "check" as const,
      detail:
        "Users on iOS 17+ / Android 13+ must tap 'Allow' — a denied permission silently drops delivery.",
    },
  ];

  const urgent = noReachableUsers
    ? "No devices registered yet. Most common cause: app is running in Expo Go, or Android FCM credentials aren't uploaded to EAS."
    : lowCoverage
      ? "Less than 30% of users have tokens — worth checking FCM/APNs credentials and testing a sign-in flow end-to-end."
      : null;

  return (
    <div
      className={cn(
        "rounded-xl border p-5",
        urgent
          ? "bg-amber-50 border-amber-200"
          : "bg-emerald-50 border-emerald-200",
      )}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
            urgent ? "bg-amber-100" : "bg-emerald-100",
          )}
        >
          {urgent ? (
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          )}
        </div>
        <div>
          <p className="font-bold text-gray-900">Setup Health</p>
          {urgent ? (
            <p className="text-sm text-amber-800 mt-0.5">{urgent}</p>
          ) : (
            <p className="text-sm text-emerald-800 mt-0.5">
              Configuration looks healthy — deliveries should flow to
              registered devices.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {checks.map((c) => {
          const statusIcon =
            c.status === "done" ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
            ) : c.status === "warn" ? (
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            );
          return (
            <div
              key={c.label}
              className="flex items-start gap-2 bg-white/70 rounded-lg px-3 py-2"
            >
              {statusIcon}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {c.label}
                </p>
                <p className="text-[11px] text-gray-600 mt-0.5">{c.detail}</p>
              </div>
              <span
                className={cn(
                  "text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0",
                  c.status === "done"
                    ? "bg-emerald-100 text-emerald-700"
                    : c.status === "warn"
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700",
                )}
              >
                {c.status === "done"
                  ? "Ready"
                  : c.status === "warn"
                    ? "Missing"
                    : "Verify"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200/70">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
          Useful links
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href="https://docs.expo.dev/push-notifications/fcm-credentials/"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-gray-200"
          >
            FCM setup guide <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://docs.expo.dev/push-notifications/push-notifications-setup/"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-gray-200"
          >
            Expo push docs <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://expo.dev/notifications"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-gray-200"
          >
            Test from Expo dashboard <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
